import type { RegistrationId, StructureSetId } from '../../changes/ids';
import type {
    ConnectorVersionToken,
    DatasetPersistenceRef,
} from '../../persistence/PersistenceRef';
import type {
    PersistedEntityId,
    PersistenceRegistry,
} from '../../persistence/PersistenceRegistry';
import type { RecordedDatasetOrigin } from '../../provenance/RecordedDatasetOrigin';
import type { SaveOutcome } from './SaveOutcome';

/**
 * A live-state mutation a saver WOULD have performed, returned as data
 * for the orchestrator to apply instead. Savers hold no write handle at
 * all (the registry they see is read-only, and they cannot reach the
 * aggregate): everything a save changes in the session travels through
 * this type, in order, through one applier. That is also what makes a
 * saver worker-offloadable — its inputs are sealed data, its outputs
 * are outcomes plus effects.
 */
export type SaveEffect =
    | {
          /** A create committed: record the row's identity + token. */
          readonly kind: 'setRef';
          readonly id: PersistedEntityId;
          readonly ref: DatasetPersistenceRef;
      }
    | {
          /** A guarded call succeeded: record the fresh version token. */
          readonly kind: 'updateVersion';
          readonly id: PersistedEntityId;
          readonly version: ConnectorVersionToken;
      }
    | {
          /** A delete committed (or the row was already gone). */
          readonly kind: 'removeRef';
          readonly id: PersistedEntityId;
      }
    | {
          /**
           * The server minted file identity for a rewritten REG; the
           * session object adopts it so an export names the stored
           * instance (the session identity deliberately does not move).
           */
          readonly kind: 'adoptRegistrationFileIdentity';
          readonly id: RegistrationId;
          readonly identity: {
              readonly studyInstanceUid?: string | null;
              readonly seriesInstanceUid?: string | null;
              readonly sopInstanceUid?: string | null;
          };
      }
    | {
          /**
           * The set's content reached the database: sync-status flip, and
           * the SOP Instance UID the database stores when the file was sent.
           */
          readonly kind: 'markStructureSetUploaded';
          readonly id: StructureSetId;
          readonly sopInstanceUid?: string | null;
      }
    | {
          /** The connector's record of how a newly saved set came to exist. */
          readonly kind: 'adoptStructureSetOrigin';
          readonly id: StructureSetId;
          readonly origin: RecordedDatasetOrigin;
      };

/**
 * One entry's result: the report-facing outcome plus the effects the
 * entry's network calls earned. Effects are applied WHATEVER the
 * outcome — a create that committed before a later step failed must
 * still record its ref, or the retry duplicates the row.
 */
export interface SaveEntryResult {
    readonly outcome: SaveOutcome;
    readonly effects: readonly SaveEffect[];
}

/**
 * The domain half of effect application — the mutations that land
 * on the live aggregate. Only the session assembly implements this
 * (with the patient and tracker in closure); savers never see it.
 * Implementations look the target up by id and silently skip one that
 * is gone — a dataset deleted mid-save has nothing left to write onto.
 */
export interface DomainEffectHooks {
    adoptRegistrationFileIdentity(
        id: RegistrationId,
        identity: {
            readonly studyInstanceUid?: string | null;
            readonly seriesInstanceUid?: string | null;
            readonly sopInstanceUid?: string | null;
        }
    ): void;
    markStructureSetUploaded(
        id: StructureSetId,
        sopInstanceUid?: string | null
    ): void;
    adoptStructureSetOrigin(
        id: StructureSetId,
        origin: RecordedDatasetOrigin
    ): void;
}

/**
 * The one applier for every effect kind, shared by production assembly
 * and test harnesses so the two can never drift apart.
 */
export function makeSaveEffectApplier(
    registry: PersistenceRegistry,
    domain: DomainEffectHooks
): (effect: SaveEffect) => void {
    return (effect) => {
        switch (effect.kind) {
            case 'setRef':
                registry.setRef(effect.id, effect.ref);
                break;
            case 'updateVersion':
                registry.updateVersion(effect.id, effect.version);
                break;
            case 'removeRef':
                registry.removeRef(effect.id);
                break;
            case 'adoptRegistrationFileIdentity':
                domain.adoptRegistrationFileIdentity(
                    effect.id,
                    effect.identity
                );
                break;
            case 'markStructureSetUploaded':
                domain.markStructureSetUploaded(
                    effect.id,
                    effect.sopInstanceUid
                );
                break;
            case 'adoptStructureSetOrigin':
                domain.adoptStructureSetOrigin(effect.id, effect.origin);
                break;
        }
    };
}
